import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { CalendarIcon, PhoneIcon, PhoneOffIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useQueryClient } from "@tanstack/react-query";

import { Column, ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { useCurrentCompanyId } from "@/hooks/useCurrentCompanyId";
import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCancelVoiceAIOutreach } from "@/hooks/mutations/useCancelVoiceAIOutreach";
import { useCreateVoiceAIOutreach } from "@/hooks/mutations/useCreateVoiceAIOutreach";
import { callHistoryQueryKey } from "@/hooks/queries/useGetCallHistory";
import { candidacyInfoQueryKey } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetVoiceAIOutreaches } from "@/hooks/queries/useGetVoiceAIOutreaches";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { VoiceAIOutreachInitiationType, VoiceAIOutreachStatus } from "@/types";

const getTodayDateInputValue = () => new Date().toLocaleDateString("en-CA");

const BUTTON_CLASSNAME = "h-8 gap-2 rounded-full px-3 text-xs font-medium";

const scheduleCallSchema = z.object({
  date: z.string().min(1, "Required"),
  time: z.string().min(1, "Required"),
});

const ScheduleCallDialog = ({
  isPending,
  onOpenChange,
  onSchedule,
  open,
}: {
  isPending: boolean;
  onOpenChange: (open: boolean) => void;
  onSchedule: (data: z.infer<typeof scheduleCallSchema>) => void;
  open: boolean;
}) => {
  /* React Hooks */
  const form = useForm<z.infer<typeof scheduleCallSchema>>({
    defaultValues: { date: "", time: "" },
    resolver: zodResolver(scheduleCallSchema),
  });

  /* Render */
  const render = () => {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-slate-700">
              Schedule Voice AI Call
            </DialogTitle>
          </DialogHeader>
          <Form {...form}>
            <form
              className="flex flex-col gap-6"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={form.handleSubmit(onSchedule)}
            >
              <Row className="gap-4">
                <FormField
                  control={form.control}
                  name="date"
                  render={({ field }) => (
                    <FormItem className="w-full text-slate-700">
                      <FormLabel>Date</FormLabel>
                      <FormControl>
                        <Input
                          min={getTodayDateInputValue()}
                          type="date"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="time"
                  render={({ field }) => (
                    <FormItem className="w-full text-slate-700">
                      <FormLabel>Time</FormLabel>
                      <FormControl>
                        <Input type="time" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </Row>
              <Row className="items-center justify-end gap-2">
                <Button
                  onClick={() => {
                    onOpenChange(false);
                  }}
                  type="button"
                  variant="secondary"
                >
                  Cancel
                </Button>
                <Button disabled={isPending} type="submit">
                  {isPending ? "Scheduling..." : "Schedule Call"}
                </Button>
              </Row>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    );
  };

  return render();
};

// Manual Stevie kickoff for the current job's candidacy — carried over from
// the old candidate sheet's dedicated Voice AI Call tab. While a call is
// scheduled the pair flips to Cancel call + a disabled Call now, so the header
// reflects the state the backend enforces (one live call per candidate).
export const KickoffAiCallActions = ({
  candidacyId,
  workerId,
}: {
  candidacyId: string;
  workerId: number;
}) => {
  /* Context */
  const currentCompanyId = useCurrentCompanyId();
  const currentJobId = useCurrentJobId();

  /* Database CRUD Operations */
  // Read
  const { data: voiceAIOutreaches } = useGetVoiceAIOutreaches({
    currentCandidacyId: candidacyId,
  });
  // Create
  const { mutate: createVoiceAIOutreach, isPending } = useCreateVoiceAIOutreach(
    { currentCandidacyId: candidacyId },
  );
  // Update
  const { mutate: cancelVoiceAIOutreach, isPending: isCancelPending } =
    useCancelVoiceAIOutreach();

  /* React Hooks */
  const [isCallConfirmationOpen, setIsCallConfirmationOpen] = useState(false);
  const [isCancelConfirmationOpen, setIsCancelConfirmationOpen] =
    useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const queryClient = useQueryClient();
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  /* Helpers */
  const pendingCall =
    voiceAIOutreaches?.find(
      (outreach) => outreach.status === VoiceAIOutreachStatus.PENDING,
    ) ?? null;

  // A pending scheduled call can be cancelled right up until it dials. The
  // backend owns that decision and its error message lands in the toast. Only
  // a direct call is ruled out here, since it is already being placed.
  const getCancelDisabledReason = () => {
    if (!pendingCall) return null;
    if (pendingCall.initiationType === VoiceAIOutreachInitiationType.DIRECT)
      return "Call is already being placed.";
    return null;
  };
  const cancelDisabledReason = getCancelDisabledReason();

  const pendingCallLabel =
    (pendingCall
      ? dates.formatIsoDateTimeForUser(pendingCall.scheduledDateTime)
      : null) ?? "an upcoming time";

  /* Handlers */
  const handleScheduleCall = (data?: z.infer<typeof scheduleCallSchema>) => {
    mixpanelUtils.trackFormSubmission(
      "Schedule voice AI call",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
      { is_scheduled: !!data },
    );
    createVoiceAIOutreach(
      {
        createVoiceAIOutreachRequest: {
          scheduledDateTime: data
            ? new Date(`${data.date}T${data.time}`).toISOString()
            : undefined,
        },
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: callHistoryQueryKey(workerId),
          });
          successToast({
            description: data
              ? "Call scheduled successfully!"
              : "Call started successfully!",
          });
          setIsCallConfirmationOpen(false);
          setIsScheduleDialogOpen(false);
        },
      },
    );
  };

  const handleCallNowClick = () => {
    mixpanelUtils.trackButtonClick(
      "Initiate Voice AI Call",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    setIsCallConfirmationOpen(true);
  };

  const handleCancelCall = () => {
    if (!pendingCall) return;
    const reason = getCancelDisabledReason();
    if (reason) {
      errorToast({ description: reason });
      setIsCancelConfirmationOpen(false);
      return;
    }
    mixpanelUtils.trackButtonClick(
      "Cancel Scheduled Stevie Call Confirm",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    cancelVoiceAIOutreach(
      { candidacyId, voiceAIOutreachId: pendingCall.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({
            queryKey: callHistoryQueryKey(workerId),
          });
          // The backend also moves a Stevie-booked text outreach to
          // CALL_CANCELLED. The sheet reads that status from candidacy info.
          void queryClient.invalidateQueries({
            queryKey: candidacyInfoQueryKey(
              currentCompanyId,
              currentJobId,
              candidacyId,
            ),
          });
          successToast({ description: "Stevie call cancelled" });
          setIsCancelConfirmationOpen(false);
        },
        onError: (error) => {
          const message = (
            error as { response?: { data?: { message?: string } } }
          ).response?.data?.message;
          errorToast({ description: message ?? "Failed to cancel the call." });
        },
      },
    );
  };

  /* Render */
  const renderWithTooltip = (
    content: string | null,
    children: React.ReactNode,
  ) => {
    if (!content) return children;
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div>{children}</div>
          </TooltipTrigger>
          <TooltipContent side="bottom">{content}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const renderScheduleOrCancel = () => {
    if (!pendingCall) {
      return (
        <Button
          className={BUTTON_CLASSNAME}
          onClick={() => {
            setIsScheduleDialogOpen(true);
          }}
          size="sm"
          variant="outlineSecondary"
        >
          <CalendarIcon className="h-4 w-4" />
          Schedule call
        </Button>
      );
    }
    return renderWithTooltip(
      cancelDisabledReason,
      <Button
        className={BUTTON_CLASSNAME}
        disabled={cancelDisabledReason !== null || isCancelPending}
        onClick={() => {
          setIsCancelConfirmationOpen(true);
        }}
        size="sm"
        variant="outlineSecondary"
      >
        <PhoneOffIcon className="h-4 w-4" />
        Cancel call
      </Button>,
    );
  };

  const render = () => {
    return (
      <Column>
        <Row className="items-center gap-2">
          {renderScheduleOrCancel()}
          {renderWithTooltip(
            pendingCall
              ? `A call is already scheduled for ${pendingCallLabel}`
              : null,
            <Button
              className={`${BUTTON_CLASSNAME} shadow-sm`}
              disabled={isPending || !!pendingCall}
              onClick={handleCallNowClick}
              size="sm"
            >
              <PhoneIcon className="h-4 w-4" />
              {isPending ? "Starting..." : "Call now"}
            </Button>,
          )}
        </Row>
        <ConfirmationDialog
          confirmButtonText="Cancel Call"
          confirmationButtonVariant="destructive"
          description={`This removes the Stevie call scheduled for ${pendingCallLabel}. Stevie stays active on this candidacy and can rebook if the candidate replies. To stop Stevie entirely, use Stop Stevie Outreach in the candidate menu.`}
          isLoading={isCancelPending}
          onConfirm={handleCancelCall}
          onOpenChange={setIsCancelConfirmationOpen}
          open={isCancelConfirmationOpen}
          title="Cancel Scheduled Stevie Call"
        />
        <ScheduleCallDialog
          isPending={isPending}
          onOpenChange={setIsScheduleDialogOpen}
          onSchedule={handleScheduleCall}
          open={isScheduleDialogOpen}
        />
        <ConfirmationDialog
          confirmButtonText="Start Call"
          description="This call will start immediately. Are you sure you want to proceed?"
          onConfirm={() => {
            handleScheduleCall();
          }}
          onOpenChange={setIsCallConfirmationOpen}
          open={isCallConfirmationOpen}
          title="Start Voice AI Call"
        />
      </Column>
    );
  };

  return render();
};
